/**
 * Complex Actions Slice
 *
 * Actions that coordinate across multiple slices.
 */

import type { StateCreator } from 'zustand';
import type { ViewMode } from '@/types';
import type { AppStore, ComplexActionsSlice, ItemType } from '../types';

export const createComplexActionsSlice: StateCreator<AppStore, [], [], ComplexActionsSlice> = (
  set,
  get
) => ({
  closeSettingsAndResetNavigation: (modal: 'space' | 'list') => {
    if (modal === 'space') {
      get().closeSpaceSettings();
    } else {
      get().closeListSettings();
    }
    get().clearNavigationSelection();
  },

  navigate: (path: string | null) => {
    get().closeAllItems();
    set({ selectedTaskId: null });

    const currentViewMode = get().viewMode;
    const newViewMode =
      currentViewMode === 'team' || currentViewMode === 'inbox' || currentViewMode === 'timesheet'
        ? 'list'
        : currentViewMode;

    get().setFilters({ path }, { skipCloseItems: true });
    if (newViewMode !== currentViewMode) {
      set({ viewMode: newViewMode });
    }
  },

  selectTask: (taskId: string) => {
    get().closeAllItems();
    set({ selectedTaskId: taskId });
  },

  handleUrlChange: (urlState: {
    viewMode: ViewMode;
    path: string | null;
    taskId: string | null;
    itemType: ItemType | null;
    itemId: string | null;
    deviationGroupBy?: 'person' | 'task' | 'space' | 'list' | null;
    deviationFilter?: 'all' | 'over' | 'under' | 'on-time';
    deviationAssignee?: string | null;
  }) => {
    set({ viewMode: urlState.viewMode });
    // Skip closing items when we're navigating to an item URL
    // The item will be loaded separately by useRouterSync
    const hasItem = !!(urlState.itemType && urlState.itemId);
    get().setFilters({ path: urlState.path }, { skipCloseItems: hasItem });
    set({ selectedTaskId: urlState.taskId });

    if (urlState.deviationGroupBy !== undefined) {
      set({ deviationGroupBy: urlState.deviationGroupBy });
    }
    if (urlState.deviationFilter !== undefined) {
      set({ deviationFilter: urlState.deviationFilter });
    }
    if (urlState.deviationAssignee !== undefined) {
      set({ deviationAssignee: urlState.deviationAssignee });
    }

    if (!urlState.itemType || !urlState.itemId) {
      get().closeAllItems();
    }
  },
});
